import { Avatar, Box, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ApiWaitingRoomLinkProfile } from 'types';
import { UserIcon } from 'assets';
import { isHipaaLink } from 'config/productProfile';
import { getHipaaLogoSrc, getVia3LogoSrc } from 'theme';

export type SessionEndReason = 'ended' | 'removed';

type SessionEndedProps = {
  reason: SessionEndReason;
  profile: ApiWaitingRoomLinkProfile;
  avatarUrl: string | null;
  hostName: string;
};

export function SessionEnded({ reason, avatarUrl, hostName }: SessionEndedProps) {
  const { t } = useTranslation('link');
  const theme = useTheme();
  const resolvedThemeMode = (theme as any).customMode || theme.palette.mode;
  const logoSrc = isHipaaLink ? getHipaaLogoSrc(resolvedThemeMode) : getVia3LogoSrc(resolvedThemeMode);

  const handleCheckIn = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
          boxShadow: theme.shadows[4],
          padding: '40px 40px 36px',
          maxWidth: '460px',
          width: '90%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          textAlign: 'center',
        }}
      >
        {/* Logo sits above the card but doesn't affect vertical centering */}
        <Box
          component='img'
          src={logoSrc}
          alt={isHipaaLink ? 'HIPAA LINK' : 'VIA3'}
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            mb: '16px',
            height: 36,
            width: 'auto',
          }}
        />
        <Avatar
          src={avatarUrl ?? undefined}
          alt={hostName}
          sx={{
            width: 150,
            height: 150,
            border: `3px solid ${theme.palette.divider}`,
            backgroundColor: 'var(--app-field-surface-disabled)',
            color: 'var(--app-text-default)',
          }}
        >
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserIcon
              sx={{ color: 'var(--app-welcome-panel-icon-fg)', fontSize: '5.6rem', width: '1.4em', height: '1.4em' }}
            />
          </Box>
        </Avatar>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <Typography variant='h4' sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.3 }}>
            {reason === 'ended' ? t('sessionEnded.sessionEndedLine1') : t('sessionEnded.removedLine1')}
            <br />
            {reason === 'ended'
              ? t('sessionEnded.sessionEndedLine2', { hostName })
              : t('sessionEnded.removedLine2', { hostName })}
          </Typography>

          {reason === 'ended' && (
            <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
              {t('sessionEnded.closeWindow')}
            </Typography>
          )}

          <Box
            sx={{
              width: '60%',
              height: '1px',
              backgroundColor: theme.palette.divider,
              mt: '4px',
            }}
          />

          <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
            {t('sessionEnded.checkBackIn')}{' '}
            <Link
              component='button'
              onClick={handleCheckIn}
              underline='always'
              sx={{
                fontSize: 'inherit',
                color: theme.palette.primary.main,
                fontWeight: 600,
                cursor: 'pointer',
                verticalAlign: 'baseline',
              }}
            >
              {t('sessionEnded.clickHere')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
